import type { PhoneNumber } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { FC, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import type { FlowCallActionT } from 'components/powerCallFlow/api/types';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { getRequestID } from 'components/shared/util/request';
import type { WithFlowsInjectedPropsT, WithQueuesInjectedProps } from 'hocs';
import { withFlows, withQueues } from 'hocs';
import { FlowCreatePayloadT } from 'hocs/flows/api/types';
import { useNotifications } from 'hooks/useNotifications';

import PrettyNumber from '../PrettyNumber';

const DEFAULT_ACTION_AXIS_X = 1800;
const DEFAULT_ACTION_AXIS_Y = 700;

const DEFAULT_ACTION_PROPS: Omit<FlowCallActionT, 'id' | 'nodeType'> = {
  badges: [],
  childIds: [],
  connectionLabel: 'arrow',
  icon: null,
  isAction: false,
  isEditable: false,
  isRemovable: false,
  overlay: [],
  parentId: null,
  payload: null,
  text: '',
  pageX: DEFAULT_ACTION_AXIS_X,
  pageY: DEFAULT_ACTION_AXIS_Y,
};

type PropsT = {
  isShown: boolean;
  onClose: () => void;
  number: PhoneNumber | null;
} & WithFlowsInjectedPropsT<FlowCallActionT> &
  WithQueuesInjectedProps;

const NewFlowModal: FC<PropsT> = ({
  isShown,
  onClose,
  number,
  flows,
  queues,
}) => {
  const notify = useNotifications();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const {
    organization: { default_queue: defaultQueueId },
  } = useSelector((state: RootReducerStateT) => state.settings.organization);

  const onCloseClick = useCallback(() => {
    ampli.phoneCreateFlowFormCancelled();
    onClose();
  }, [onClose]);

  const onConfirm = useCallback(async () => {
    ampli.phoneCreateFlowFormSubmitted();

    if (!number) {
      return;
    }

    setIsLoading(true);

    const initNodeId = getRequestID();
    const queueNodeId = getRequestID();

    try {
      const { data: defaultQueue } = await queues.getQueue(defaultQueueId);
      const newFlow: FlowCreatePayloadT<FlowCallActionT> = {
        channel: 'pstn_phone',
        active: false,
        location: number.e164_format,
        name: `Call flow for ${number.e164_format}`,
        actions: [
          {
            ...DEFAULT_ACTION_PROPS,
            id: initNodeId,
            nodeType: FlowNodeTypes.INIT,
            text: 'Incoming Call',
          },
          {
            ...DEFAULT_ACTION_PROPS,
            icon: 'users',
            id: queueNodeId,
            isEditable: true,
            isRemovable: true,
            nodeType: FlowNodeTypes.QUEUE,
            pageY: DEFAULT_ACTION_AXIS_Y + 120,
            payload: {
              queue: {
                label: defaultQueue.name,
                value: defaultQueue.id,
              },
              language: 'en-GB',
            },
            parentId: initNodeId,
            text: defaultQueue.name,
          },
        ],
      };

      const {
        data: { id, name },
      } = await flows.create(newFlow);

      notify({
        title: 'Flow created',
        text: `Flow was succesfully created as "${name}"`,
        className: 'success',
        time: 5000,
        icon: 'done',
      });

      navigate(`/settings/phone/flow/${id}`);
    } catch {
      notify({
        title: 'An error occurred',
        text: 'An error occurred when trying to create the flow',
        icon: 'exclamation',
        className: 'danger',
      });
    }

    onClose();
  }, [
    setIsLoading,
    defaultQueueId,
    flows,
    navigate,
    notify,
    number,
    queues,
    onClose,
  ]);

  return (
    <ConfirmModal
      isShown={isShown}
      headline="New flow"
      cancelText="Cancel"
      confirmText="Create"
      confirmProps={{ isLoading, variant: 'primary' }}
      cancel={onCloseClick}
      confirm={onConfirm}
    >
      <span>
        Create new flow for number{' '}
        <strong>
          <PrettyNumber number={number} />
        </strong>
        ?
      </span>
    </ConfirmModal>
  );
};

export default withQueues(withFlows(NewFlowModal));
